import React, { useState, useEffect } from 'react';
import { courseService, userService } from '../services';
import { validators } from '../utils/validators';

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ courseName: '', instructor: '', fieldOfStudy: '' });

  useEffect(() => { fetchCourses(); fetchUsers(); }, []);

  useEffect(() => {
    if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 4000); return () => clearTimeout(t); }
  }, [successMessage]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  const fetchCourses = async () => {
    try { setLoading(true); setError(''); const data = await courseService.getAllCourses(); setCourses(data); }
    catch (err) { setError(err.error || err.message || 'Failed to fetch courses'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try { const data = await userService.getAllUsers(); setUsers(data); }
    catch (err) { console.error('Error loading users:', err); }
  };

  const validateCourseForm = () => {
    let msg = validators.required(formData.courseName?.trim(), 'Course name');
    if (msg) return msg;
    msg = validators.required(formData.instructor, 'Instructor');
    if (msg) return msg;
    const name = formData.courseName?.trim().toLowerCase();
    const duplicate = courses.find(c => c.courseName?.toLowerCase() === name && c._id !== selectedCourse?._id);
    if (duplicate) return 'A course with this name already exists.';
    return null;
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage('');
    const ve = validateCourseForm(); if (ve) { setError(ve); return; }
    try {
      await courseService.createCourse({ 
        courseName: formData.courseName.trim(), 
        fieldOfStudy: formData.fieldOfStudy.trim(),
        ...(formData.instructor && { instructor: formData.instructor }) 
      });
      setSuccessMessage('Course created successfully!'); resetForm(); fetchCourses();
    } catch (err) { setError(err.error || err.message || 'Failed to create course'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage('');
    if (!selectedCourse) return;
    const ve = validateCourseForm(); if (ve) { setError(ve); return; }
    try {
      await courseService.updateCourse(selectedCourse._id, { 
        courseName: formData.courseName.trim(), 
        fieldOfStudy: formData.fieldOfStudy.trim(),
        ...(formData.instructor && { instructor: formData.instructor }) 
      });
      setSuccessMessage('Course updated successfully!'); resetForm(); fetchCourses();
    } catch (err) { setError(err.error || err.message || 'Failed to update course'); }
  };

  const handleDelete = async (courseId) => {
    setError(''); setSuccessMessage('');
    try { await courseService.deleteCourse(courseId); setSuccessMessage('Course deleted.'); setDeleteConfirm(null); fetchCourses(); }
    catch (err) { setError(err.error || err.message || 'Failed to delete course'); setDeleteConfirm(null); }
  };

  const handleEdit = (course) => {
    setSelectedCourse(course);
    setFormData({ 
      courseName: course.courseName || '', 
      instructor: course.instructor?._id || course.instructor || '',
      fieldOfStudy: course.fieldOfStudy || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ courseName: '', instructor: '', fieldOfStudy: '' });
    setSelectedCourse(null); setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const filteredCourses = courses.filter(c =>
    c.courseName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.fieldOfStudy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.instructor?.username && c.instructor.username.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getInstructorName = (course) => {
    if (!course.instructor) return null;
    return typeof course.instructor === 'object' ? course.instructor.username : null;
  };

  const getInstructorEmail = (course) => {
    if (!course.instructor || typeof course.instructor !== 'object') return null;
    return course.instructor.email || null;
  };

  const courseColors = [
    '#1e40af',
    '#7c3aed', 
    '#dc2626',
    '#059669',
    '#ea580c',
    '#0891b2',
    '#c2410c',
    '#0d9488',
  ];
  const getCourseColor = (name) => courseColors[(name?.charCodeAt(0) || 0) % courseColors.length];
  const getCourseInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : 'C';

  const getInstructorInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
  const instrColors = ['#1e40af', '#ea580c', '#059669', '#dc2626'];
  const getInstrColor = (name) => instrColors[(name?.charCodeAt(0) || 0) % instrColors.length];

  return (
    <>
      <style>{`
        
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .cm-root {
          
          --primary-blue: #1e40af;
          --secondary-blue: #3b82f6;
          --accent-orange: #ea580c;
          --text-dark: #111827;
          --text-medium: #374151;
          --text-light: #6b7280;
          --border-light: #e5e7eb;
          --border-medium: #d1d5db;
          --background-gray: #f8fafc;
          --background-white: #ffffff;
          --success-green: #059669;
          --error-red: #dc2626;
          --warning-yellow: #d97706;
          --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1);
          
          background: var(--background-gray);
          color: var(--text-dark);
          font-family: system-ui, -apple-system, 'Inter', sans-serif;
          min-height: 100vh;
          padding: 28px 24px;
        }

        /* ── TOAST ── */
        .cm-toast {
          position: fixed; top: 20px; right: 20px; z-index: 1000;
          display: flex; align-items: center; gap: 10px;
          padding: 14px 20px; border-radius: 6px;
          font-size: 13px; font-weight: 600;
          box-shadow: var(--shadow-md);
          animation: cmSlideIn 0.3s ease;
          max-width: 360px; pointer-events: none;
        }
        .cm-toast-success { background: #f0fdf4; border: 1px solid #d1fae5; color: var(--success-green); border-left: 4px solid var(--success-green); }
        .cm-toast-error { background: #fef2f2; border: 1px solid #fecaca; color: var(--error-red); border-left: 4px solid var(--error-red); }
        @keyframes cmSlideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }

        /* ── PAGE HEADER ── */
        .cm-page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 20px; margin-bottom: 28px; flex-wrap: wrap;
        }
        .cm-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(251,191,36,0.08); border: 1px solid rgba(251,191,36,0.2);
          border-radius: 12px; padding: 4px 12px;
          font-size: 11px; font-weight: 700; color: var(--accent-orange);
          letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 10px;
        }
        .cm-page-title {
          font-family: inherit;
          font-size: 28px; font-weight: 800; letter-spacing: -0.5px; line-height: 1.1;
        }
        .cm-page-title span {
          color: var(--primary-blue);
        }
        .cm-page-sub { font-size: 13px; color: var(--text-light); margin-top: 6px; }

        .cm-header-stats { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-start; }
        .cm-mini-stat {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 12px 18px; text-align: center; min-width: 80px;
        }
        .cm-mini-stat-num {
          font-family: inherit; font-size: 22px; font-weight: 800;
          color: var(--text-dark);
        }
        .cm-mini-stat-label { font-size: 10px; color: var(--text-light); font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }

        /* ── TOOLBAR ── */
        .cm-toolbar {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 24px; flex-wrap: wrap;
        }
        .cm-search {
          flex: 1; min-width: 200px; max-width: 420px;
          display: flex; align-items: center; gap: 10px;
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 11px 16px;
          transition: border-color 0.2s;
        }
        .cm-search:focus-within { border-color: var(--border-hi); }
        .cm-search-icon { font-size: 15px; color: var(--text-light); flex-shrink: 0; }
        .cm-search input {
          flex: 1; border: none; outline: none; background: none;
          font-size: 13px; color: var(--text-dark); font-family: inherit;
        }
        .cm-search input::placeholder { color: var(--text-light); }
        .cm-clear-btn { background: none; border: none; color: var(--text-light); cursor: pointer; font-size: 15px; padding: 0 2px; transition: color 0.2s; }
        .cm-clear-btn:hover { color: var(--text-dark); }

        .cm-btn-add {
          display: flex; align-items: center; gap: 8px; padding: 11px 20px;
          background: linear-gradient(135deg, var(--accent-orange), var(--error-red));
          color: white; border: none; border-radius: 6px;
          font-size: 13px; font-weight: 700; cursor: pointer;
          font-family: inherit; letter-spacing: 0.2px;
          transition: all 0.2s; white-space: nowrap;
        }
        .cm-btn-add:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: var(--shadow-md); }
        .cm-btn-add.cancel {
          background: rgba(248,113,113,0.1); color: var(--error-red);
          border: 1px solid rgba(248,113,113,0.3); box-shadow: none;
        }
        .cm-btn-add.cancel:hover { background: rgba(248,113,113,0.15); transform: none; }
        .cm-btn-refresh {
          display: flex; align-items: center; gap: 6px; padding: 11px 16px;
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 6px; color: var(--muted2);
          font-size: 13px; font-weight: 500; cursor: pointer;
          font-family: inherit; transition: all 0.2s; white-space: nowrap;
        }
        .cm-btn-refresh:hover { border-color: var(--border-light); color: var(--text-dark); }

        /* ── FORM PANEL ── */
        .cm-form-panel {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; margin-bottom: 28px; overflow: hidden;
          animation: cmExpandIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes cmExpandIn { from { opacity:0; transform:scaleY(0.95) translateY(-8px); transform-origin:top; } to { opacity:1; transform:scaleY(1) translateY(0); } }

        .cm-form-header {
          display: flex; align-items: center; gap: 14px;
          padding: 20px 28px; border-bottom: 1px solid var(--border);
          background: linear-gradient(135deg, rgba(251,191,36,0.06), rgba(244,114,182,0.04));
        }
        .cm-form-icon {
          width: 38px; height: 38px; border-radius: 8px;
          background: linear-gradient(135deg, var(--accent-orange), var(--error-red));
          display: flex; align-items: center; justify-content: center; font-size: 18px;
          flex-shrink: 0;
        }
        .cm-form-title { font-family: inherit; font-size: 17px; font-weight: 700; }
        .cm-form-sub { font-size: 12px; color: var(--text-light); margin-top: 2px; }

        .cm-form-body { padding: 28px; display: flex; flex-direction: column; gap: 20px; }

        .cm-field { display: flex; flex-direction: column; gap: 8px; }
        .cm-label {
          font-size: 11px; font-weight: 700; color: var(--muted2);
          text-transform: uppercase; letter-spacing: 0.6px;
          display: flex; align-items: center; gap: 4px;
        }
        .cm-req { color: var(--accent-orange); font-size: 14px; line-height: 1; }

        .cm-input, .cm-select {
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 6px; padding: 13px 16px;
          font-size: 14px; color: var(--text-dark);
          font-family: inherit; outline: none; width: 100%;
          transition: border-color 0.2s, background 0.2s;
        }
        .cm-input:focus, .cm-select:focus { border-color: rgba(251,191,36,0.4); background: rgba(251,191,36,0.03); }
        .cm-input::placeholder { color: var(--text-light); }
        .cm-select {
          cursor: pointer; appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 14px center;
          padding-right: 36px;
        }
        .cm-select option { background: #161B2E; color: var(--text-dark); }

        .cm-input-hint { font-size: 11px; color: var(--text-light); margin-top: 2px; }

        /* Instructor preview chip */
        .cm-instr-preview {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 14px; margin-top: 8px;
          background: rgba(96,165,250,0.06);
          border: 1px solid rgba(96,165,250,0.15);
          border-radius: 6px;
        }
        .cm-instr-av {
          width: 30px; height: 30px; border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: white;
          font-family: inherit; flex-shrink: 0;
        }
        .cm-instr-preview-name { font-size: 13px; font-weight: 600; }
        .cm-instr-preview-email { font-size: 11px; color: var(--text-light); }

        .cm-form-footer {
          display: flex; align-items: center; justify-content: flex-end;
          gap: 10px; padding: 20px 28px; border-top: 1px solid var(--border);
        }
        .cm-btn-submit {
          display: flex; align-items: center; gap: 8px; padding: 12px 28px;
          background: linear-gradient(135deg, var(--accent-orange), var(--error-red));
          color: white; border: none; border-radius: 6px;
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
        }
        .cm-btn-submit:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: var(--shadow-md); }
        .cm-btn-cancel {
          padding: 12px 22px; background: var(--border-light);
          border: 1px solid var(--border-light); border-radius: 6px;
          color: var(--muted2); font-size: 14px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: all 0.2s;
        }
        .cm-btn-cancel:hover { background: var(--border-light); color: var(--text-dark); }

        /* ── COURSE GRID ── */
        .cm-section-head {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 16px;
        }
        .cm-section-title {
          font-family: inherit; font-size: 17px; font-weight: 700;
          display: flex; align-items: center; gap: 8px;
        }
        .cm-section-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: var(--accent-orange); box-shadow: 0 0 8px var(--accent-orange);
        }
        .cm-count-badge {
          background: rgba(251,191,36,0.12); border: 1px solid rgba(251,191,36,0.2);
          color: var(--accent-orange); border-radius: 12px;
          padding: 2px 10px; font-size: 12px; font-weight: 700;
        }
        .cm-search-hint { font-size: 12px; color: var(--text-light); }
        .cm-search-hint span { color: var(--accent-orange); }

        /* Course Cards Grid */
        .cm-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .cm-course-card {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; overflow: hidden;
          transition: all 0.25s; cursor: default;
          animation: cmFadeUp 0.3s ease both;
        }
        .cm-course-card:hover {
          transform: translateY(-3px); border-color: var(--border-light);
          box-shadow: var(--shadow-md);
        }
        @keyframes cmFadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }

        .cm-card-top {
          padding: 20px 20px 16px;
          display: flex; align-items: flex-start; gap: 14px;
          position: relative;
        }
        .cm-card-badge {
          width: 52px; height: 52px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; font-weight: 800; color: white;
          font-family: inherit; flex-shrink: 0;
          letter-spacing: -0.5px;
          box-shadow: var(--shadow-md);
        }
        .cm-card-info { flex: 1; min-width: 0; }
        .cm-card-name {
          font-family: inherit; font-size: 15px; font-weight: 700;
          line-height: 1.3; margin-bottom: 4px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .cm-card-id { font-size: 10px; color: var(--text-light); font-weight: 500; letter-spacing: 0.3px; }

        .cm-card-divider { height: 1px; background: var(--border); margin: 0 20px; }

        .cm-card-bottom { padding: 14px 20px 16px; }
        .cm-card-instr-label { font-size: 10px; font-weight: 700; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.7px; margin-bottom: 8px; }
        .cm-card-instr {
          display: flex; align-items: center; gap: 10px;
        }
        .cm-card-instr-av {
          width: 32px; height: 32px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: white;
          font-family: inherit; flex-shrink: 0;
        }
        .cm-card-instr-name { font-size: 13px; font-weight: 600; }
        .cm-card-instr-email { font-size: 11px; color: var(--text-light); }
        .cm-card-no-instr {
          display: flex; align-items: center; gap: 8px;
          font-size: 12px; color: var(--text-light); font-style: italic;
        }
        .cm-card-no-instr-icon {
          width: 32px; height: 32px; border-radius: 8px;
          background: var(--border-light); border: 1px dashed var(--border-light);
          display: flex; align-items: center; justify-content: center; font-size: 14px;
        }

        .cm-card-actions {
          display: flex; gap: 8px; padding: 0 20px 18px;
        }
        .cm-btn-edit, .cm-btn-del {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
          padding: 9px 12px; border-radius: 6px;
          font-size: 12px; font-weight: 600; cursor: pointer;
          font-family: inherit; transition: all 0.2s; border: 1px solid;
        }
        .cm-btn-edit {
          background: rgba(96,165,250,0.08); border-color: rgba(96,165,250,0.2); color: var(--accent);
        }
        .cm-btn-edit:hover { background: rgba(96,165,250,0.15); border-color: rgba(96,165,250,0.4); }
        .cm-btn-del {
          background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.2); color: var(--error-red);
        }
        .cm-btn-del:hover { background: rgba(248,113,113,0.15); border-color: rgba(248,113,113,0.4); }

        /* ── TABLE (desktop fallback) ── */
        .cm-table-card {
          background: var(--background-white); border: 1px solid var(--border-light);
          border-radius: 12px; overflow: hidden;
        }
        .cm-table-top {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 24px; border-bottom: 1px solid var(--border);
          flex-wrap: wrap; gap: 12px;
          background: linear-gradient(135deg, rgba(251,191,36,0.04), transparent);
        }
        .cm-scroll { overflow-x: auto; }
        .cm-scroll::-webkit-scrollbar { height: 4px; }
        .cm-scroll::-webkit-scrollbar-thumb { background: var(--border-light); border-radius: 6px; }
        .cm-table { width: 100%; border-collapse: collapse; min-width: 500px; }
        .cm-table th {
          padding: 13px 20px; font-size: 10px; font-weight: 800; color: var(--text-light);
          text-transform: uppercase; letter-spacing: 1px;
          border-bottom: 1px solid var(--border); text-align: left;
          background: var(--border-light); white-space: nowrap;
        }
        .cm-table td {
          padding: 14px 20px; font-size: 13px; color: var(--text-dark);
          border-bottom: 1px solid var(--border-light); vertical-align: middle;
        }
        .cm-table tbody tr { transition: background 0.15s; }
        .cm-table tbody tr:hover { background: rgba(251,191,36,0.03); }
        .cm-table tbody tr:last-child td { border-bottom: none; }
        .cm-course-cell { display: flex; align-items: center; gap: 10px; }
        .cm-cell-badge {
          width: 36px; height: 36px; border-radius: 8px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 800; color: white; font-family: inherit;
        }
        .cm-cell-name { font-weight: 600; }
        .cm-instr-cell { display: flex; align-items: center; gap: 8px; }
        .cm-instr-cell-av {
          width: 28px; height: 28px; border-radius: 6px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-size: 10px; font-weight: 800; color: white; font-family: inherit;
        }
        .cm-td-actions { display: flex; gap: 6px; }

        /* ── EMPTY / LOADING ── */
        .cm-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 72px 24px; gap: 12px;
        }
        .cm-empty-icon { font-size: 52px; opacity: 0.25; }
        .cm-empty-title { font-family: inherit; font-size: 18px; font-weight: 700; color: var(--text-light); }
        .cm-empty-sub { font-size: 13px; color: var(--text-light); text-align: center; }
        .cm-loading { display: flex; align-items: center; justify-content: center; padding: 72px; gap: 8px; }
        .cm-dot { width: 8px; height: 8px; border-radius: 50%; animation: cmBounce 1.2s ease-in-out infinite; }
        .cm-dot:nth-child(1) { background: var(--accent-orange); }
        .cm-dot:nth-child(2) { background: var(--error-red); animation-delay: 0.2s; }
        .cm-dot:nth-child(3) { background: var(--accent); animation-delay: 0.4s; }
        @keyframes cmBounce { 0%,80%,100% { transform:scale(0.6); opacity:0.4; } 40% { transform:scale(1); opacity:1; } }

        /* ── DELETE MODAL ── */
        .cm-modal-overlay {
          position: fixed; inset: 0; z-index: 200;
          background: rgba(0,0,0,0.72); backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center; padding: 20px;
          animation: cmFadeIn 0.2s ease;
        }
        @keyframes cmFadeIn { from { opacity:0; } to { opacity:1; } }
        .cm-modal {
          background: var(--background-white); border: 1px solid rgba(248,113,113,0.2);
          border-radius: 12px; padding: 32px; max-width: 420px; width: 100%;
          box-shadow: var(--shadow-md);
          animation: cmModalIn 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes cmModalIn { from { opacity:0; transform:scale(0.92); } to { opacity:1; transform:scale(1); } }
        .cm-modal-icon {
          width: 52px; height: 52px; border-radius: 12px;
          background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25);
          display: flex; align-items: center; justify-content: center;
          font-size: 24px; margin-bottom: 18px;
        }
        .cm-modal-title { font-family: inherit; font-size: 18px; font-weight: 700; margin-bottom: 8px; }
        .cm-modal-sub { font-size: 13px; color: var(--text-light); margin-bottom: 24px; line-height: 1.6; }
        .cm-modal-name {
          display: inline-block; padding: 4px 10px;
          background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.2);
          border-radius: 6px; color: #FCA5A5; font-weight: 600;
        }
        .cm-modal-actions { display: flex; gap: 10px; }
        .cm-modal-del {
          flex: 1; padding: 12px;
          background: rgba(248,113,113,0.12); border: 1px solid rgba(248,113,113,0.3);
          border-radius: 6px; color: var(--error-red);
          font-size: 14px; font-weight: 700; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
        }
        .cm-modal-del:hover { background: rgba(248,113,113,0.2); }
        .cm-modal-keep {
          flex: 1; padding: 12px;
          background: var(--surface2); border: 1px solid var(--border-light);
          border-radius: 6px; color: var(--muted2);
          font-size: 14px; font-weight: 600; cursor: pointer;
          font-family: inherit; transition: all 0.2s;
        }
        .cm-modal-keep:hover { color: var(--text-dark); }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .cm-root { padding: 16px; }
          .cm-page-title { font-size: 22px; }
          .cm-form-body { padding: 20px; }
          .cm-form-footer { padding: 16px 20px; }
          .cm-form-header { padding: 16px 20px; }
          .cm-grid { grid-template-columns: 1fr; }
          .cm-header-stats { display: none; }
        }
        @media (max-width: 480px) {
          .cm-toolbar { flex-direction: column; align-items: stretch; }
          .cm-search { max-width: none; }
          .cm-btn-add, .cm-btn-refresh { justify-content: center; }
        }
      `}</style>

      {successMessage && <div className="cm-toast cm-toast-success">✓ {successMessage}</div>}
      {error && <div className="cm-toast cm-toast-error">⚠ {error}</div>}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="cm-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <div className="cm-modal-icon">🗑️</div>
            <div className="cm-modal-title">Delete Course?</div>
            <div className="cm-modal-sub">
              This will permanently remove{' '}
              <span className="cm-modal-name">{deleteConfirm.courseName}</span>{' '}
              and all associated records. This cannot be undone.
            </div>
            <div className="cm-modal-actions">
              <button className="cm-modal-keep" onClick={() => setDeleteConfirm(null)}>Keep Course</button>
              <button className="cm-modal-del" onClick={() => handleDelete(deleteConfirm._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="cm-root">
        {/* Page Header */}
        <div className="cm-page-header">
          <div>
            <div className="cm-eyebrow">✦ Admin Panel</div>
            <h1 className="cm-page-title">Course <span>Management</span></h1>
            <p className="cm-page-sub">Create, edit and organize all training courses</p>
          </div>
          <div className="cm-header-stats">
            <div className="cm-mini-stat">
              <div className="cm-mini-stat-num">{courses.length}</div>
              <div className="cm-mini-stat-label">Total</div>
            </div>
            <div className="cm-mini-stat">
              <div className="cm-mini-stat-num">{courses.filter(c => c.instructor).length}</div>
              <div className="cm-mini-stat-label">Assigned</div>
            </div>
            <div className="cm-mini-stat">
              <div className="cm-mini-stat-num">{courses.filter(c => !c.instructor).length}</div>
              <div className="cm-mini-stat-label">Unassigned</div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="cm-toolbar">
          <div className="cm-search">
            <span className="cm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by course name or instructor..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button className="cm-clear-btn" onClick={() => setSearchTerm('')}>✕</button>}
          </div>
          <button
            className={`cm-btn-add${showForm ? ' cancel' : ''}`}
            onClick={() => { if (showForm) { resetForm(); } else { setShowForm(true); } }}
          >
            {showForm ? '✕  Cancel' : '+ Add Course'}
          </button>
          <button className="cm-btn-refresh" onClick={fetchCourses}>↻ Refresh</button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="cm-modal-overlay" onClick={resetForm}>
            <div className="cm-form-panel" onClick={e => e.stopPropagation()}>
              <div className="cm-form-header">
                <div className="cm-form-icon">{selectedCourse ? '✏️' : '📚'}</div>
                <div>
                  <div className="cm-form-title">{selectedCourse ? 'Edit Course' : 'Add New Course'}</div>
                  <div className="cm-form-sub">
                    {selectedCourse ? `Editing: ${selectedCourse.courseName}` : 'Fill in the details to create a new course'}
                  </div>
                </div>
              </div>

              <form onSubmit={selectedCourse ? handleUpdate : handleCreate}>
                <div className="cm-form-body">
                  <div className="cm-field">
                    <label className="cm-label">Course Name <span className="cm-req">*</span></label>
                    <input
                      className="cm-input"
                      type="text"
                      name="courseName"
                      value={formData.courseName}
                      onChange={handleInputChange}
                      placeholder="e.g. Web Development, Data Analysis, Graphic Design"
                      required
                    />
                    <div className="cm-input-hint">Use a clear, descriptive name students will recognise</div>
                  </div>

                  <div className="cm-field">
                    <label className="cm-label">Field of Study</label>
                    <input
                      className="cm-input"
                      type="text"
                      name="fieldOfStudy"
                      value={formData.fieldOfStudy}
                      onChange={handleInputChange}
                      placeholder="e.g. Computer Science, Business, Electrical Engineering"
                    />
                    <div className="cm-input-hint">Used for bulk enrollment filtering</div>
                  </div>

                  <div className="cm-field">
                    <label className="cm-label">Instructor <span className="cm-req">*</span></label>
                    <select className="cm-select" name="instructor" value={formData.instructor} onChange={handleInputChange}>
                      <option value="">— No instructor assigned —</option>
                      {users.map(user => (
                        <option key={user._id} value={user._id}>
                          {user.username}{user.email ? ` (${user.email})` : ''}
                        </option>
                      ))}
                    </select>
                    {/* Instructor preview */}
                    {formData.instructor && (() => {
                      const u = users.find(u => u._id === formData.instructor);
                      if (!u) return null;
                      return (
                        <div className="cm-instr-preview">
                          <div className="cm-instr-av" style={{ background: getInstrColor(u.username) }}>
                            {getInstructorInitials(u.username)}
                          </div>
                          <div>
                            <div className="cm-instr-preview-name">{u.username}</div>
                            {u.email && <div className="cm-instr-preview-email">{u.email}</div>}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="cm-form-footer">
                  <button type="button" className="cm-btn-cancel" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="cm-btn-submit">
                    {selectedCourse ? '✓ Update Course' : '✓ Create Course'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Course List */}
        <div className="cm-section-head">
          <div className="cm-section-title">
            <span className="cm-section-dot" />
            All Courses
            <span className="cm-count-badge">{filteredCourses.length}</span>
          </div>
          {searchTerm && (
            <span className="cm-search-hint">
              Results for "<span>{searchTerm}</span>"
            </span>
          )}
        </div>

        {loading ? (
          <div className="cm-loading"><div className="cm-dot"/><div className="cm-dot"/><div className="cm-dot"/></div>
        ) : filteredCourses.length === 0 ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '20px' }}>
            <div className="cm-empty">
              <span className="cm-empty-icon">📚</span>
              <div className="cm-empty-title">{searchTerm ? 'No courses found' : 'No courses yet'}</div>
              <div className="cm-empty-sub">{searchTerm ? 'Try a different search term' : 'Click "+ Add Course" to create the first course'}</div>
            </div>
          </div>
        ) : (
          <div className="cm-grid">
            {filteredCourses.map((course, i) => {
              const instrName = getInstructorName(course);
              const instrEmail = getInstructorEmail(course);
              return (
                <div key={course._id} className="cm-course-card" style={{ animationDelay: `${i * 40}ms` }}>
                  <div className="cm-card-top">
                    <div className="cm-card-badge" style={{ background: getCourseColor(course.courseName) }}>
                      {getCourseInitials(course.courseName)}
                    </div>
                    <div className="cm-card-info">
                      <div className="cm-card-name" title={course.courseName}>{course.courseName}</div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <div className="cm-card-id">ID: {course._id?.slice(-6).toUpperCase()}</div>
                        {course.fieldOfStudy && (
                          <div style={{ fontSize: '10px', background: 'rgba(52,211,153,0.1)', color: 'var(--success-green)', padding: '1px 6px', borderRadius: '10px', fontWeight: '600' }}>
                            {course.fieldOfStudy}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cm-card-divider" />

                  <div className="cm-card-bottom">
                    <div className="cm-card-instr-label">Instructor</div>
                    {instrName ? (
                      <div className="cm-card-instr">
                        <div className="cm-card-instr-av" style={{ background: getInstrColor(instrName) }}>
                          {getInstructorInitials(instrName)}
                        </div>
                        <div>
                          <div className="cm-card-instr-name">{instrName}</div>
                          {instrEmail && <div className="cm-card-instr-email">{instrEmail}</div>}
                        </div>
                      </div>
                    ) : (
                      <div className="cm-card-no-instr">
                        <div className="cm-card-no-instr-icon">👤</div>
                        <span>No instructor assigned</span>
                      </div>
                    )}
                  </div>

                  <div className="cm-card-actions">
                    <button className="cm-btn-edit" onClick={() => handleEdit(course)}>✏ Edit</button>
                    <button className="cm-btn-del" onClick={() => setDeleteConfirm(course)}>🗑 Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default CourseManagement;